import { FC, MouseEventHandler } from 'react';
import { graphql, useFragment } from 'react-relay';
import { VERTICAL_OFFSET_RESP_IMG, SRC_SET_SIZES_TILE } from '../../../constants/imageConstants';

// components
import { ItemTile } from 'dibs-search-product-tile/exports/ItemTile';
import { FavoritesItem } from '../../../components/global/FavoriteIcons/FavoritesItem';
import FavoritesItemProvider from 'dibs-portfolio-folder/exports/FavoritesItemProvider';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';

// styles
import styles from './PdpRespSharedCarouselItemTile.scss';

// types
import { FavoritesProviderChildrenProps } from '../../../components/global/FavoriteIcons/FavoritesProviderChildrenProps';
import { PdpRespSharedCarouselItemTile_item$key as Item } from './__generated__/PdpRespSharedCarouselItemTile_item.graphql';
import { PdpRespSharedCarouselItemTile_user$key as User } from './__generated__/PdpRespSharedCarouselItemTile_user.graphql';
import { PdpRespSharedCarouselItemTile_viewer$key as Viewer } from './__generated__/PdpRespSharedCarouselItemTile_viewer.graphql';
import { useQuickView } from 'dibs-search-product-tile/exports/QuickViewProvider';

const viewerFragment = graphql`
    fragment PdpRespSharedCarouselItemTile_viewer on Viewer {
        ...ItemTile_viewer
    }
`;

const itemFragment = graphql`
    fragment PdpRespSharedCarouselItemTile_item on Item {
        serviceId
        isFurniture
        firstPhotoWebPath(size: thumb)
        ...ItemTile_item @arguments(isTrade: $isTrade, priceBookName: $priceBookName)
        ...FavoritesItem_item
        ...FavoritesItemProvider_item
    }
`;

const userFragment = graphql`
    fragment PdpRespSharedCarouselItemTile_user on User {
        canShowSellerName
        ...FavoritesItemProvider_user
    }
`;

export const PdpRespSharedCarouselItemTile: FC<{
    item: Item;
    index: number;
    viewer: Viewer;
    currency: string;
    user: User | null | undefined;
    onClick?: MouseEventHandler;
    favorites: FavoritesProviderChildrenProps;
}> = ({ index, onClick, currency, favorites, user: userRef, item: itemRef, viewer: viewerRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const { openQuickView, closeQuickView } = useQuickView();
    const { canShowSellerName } = user || {};
    const canShowCreatorName = !canShowSellerName && item.isFurniture;
    const { isItemTileShorterHeight } = useServerVarsContext();

    return (
        <div className={styles.item}>
            <FavoritesItemProvider item={item} user={user} applyMouseEvents>
                {({ isFolderVisible, ...favoritesItemProps }) => {
                    const favoritesComponent = favorites ? (
                        <div className={styles.favorites}>
                            <FavoritesItem
                                item={item}
                                wrapperClass={styles.favoritesWrapper}
                                itemImage={item.firstPhotoWebPath || ''}
                                heartIconWrapperClass={styles.favoritesIconWrapper}
                                folderIconWrapperClass={styles.favoritesIconWrapper}
                                {...favorites}
                                fetchFolder={favorites.fetchFolder && !!isFolderVisible}
                                {...favoritesItemProps}
                            />
                        </div>
                    ) : null;

                    return (
                        <ItemTile
                            showInfo
                            item={item}
                            index={index}
                            disableBorder
                            showAuctionBids
                            viewer={viewer}
                            onClick={onClick}
                            useLoFiLazyLoader
                            hideImagePadding
                            itemSearch={null}
                            currency={currency}
                            disableContentMargin
                            showMSKUSwatch={false}
                            imageSize="imageXSmall"
                            showMeasurements={false}
                            srcSetSizes={SRC_SET_SIZES_TILE}
                            showSellerName={canShowSellerName}
                            showCreatorName={canShowCreatorName}
                            favoritesComponent={favoritesComponent}
                            imageLoadVerticalOffset={VERTICAL_OFFSET_RESP_IMG}
                            showQuickView={!!openQuickView && !!closeQuickView}
                            isItemTileShorterHeight={isItemTileShorterHeight}
                        />
                    );
                }}
            </FavoritesItemProvider>
        </div>
    );
};
